import React, { useState, useEffect } from 'react';
import { Utensils, Plus, Edit, Trash2, Search, Filter, Save, X, AlertCircle, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MenuItemManagement = () => {
  const [items, setItems] = useState([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories] = useState([]);

  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('current_organization_id');
  const locationId = localStorage.getItem('current_location_id');

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const fetchMenuItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/api/menu-items${locationId ? `?location_id=${locationId}` : ''}`;
      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId ? { 'X-Location-Id': locationId } : {})
        }
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setCustomFieldDefinitions(data.custom_field_definitions || []);
        
        // Extract unique categories
        const uniqueCategories = [...new Set((data.items || []).map(item => item.category))];
        setCategories(uniqueCategories);
      } else {
        setError('Failed to fetch menu items');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveItem = async (itemData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const url = editingItem 
        ? `${API_URL}/api/menu-items/${editingItem.id}`
        : `${API_URL}/api/menu-items`;
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId ? { 'X-Location-Id': locationId } : {}),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(itemData)
      });

      if (res.ok) {
        setSuccess(editingItem ? 'Menu item updated successfully' : 'Menu item created successfully');
        setShowAddModal(false);
        setEditingItem(null);
        await fetchMenuItems();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to save menu item');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/menu-items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId ? { 'X-Location-Id': locationId } : {})
        }
      });

      if (res.ok) {
        setSuccess('Menu item deleted successfully');
        await fetchMenuItems();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to delete menu item');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !filterCategory || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading && items.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Menu Items</h1>
            <p className="text-gray-600">Manage your menu items and custom fields</p>
          </div>
          <button
            onClick={() => {
              setEditingItem(null);
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Menu Item</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center space-x-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-center space-x-2">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search menu items..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Menu Items List */}
      {filteredItems.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <Utensils className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No menu items found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || filterCategory ? 'Try adjusting your filters' : 'Get started by adding your first menu item'}
          </p>
          {!searchTerm && !filterCategory && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add Menu Item
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <div key={item.id} className="bg-white shadow rounded-lg overflow-hidden">
              {item.image_url && (
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                  }}
                />
              )}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-900">{item.name}</h3>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {item.category}
                    </span>
                    {item.plu && (
                      <span className="ml-2 text-xs text-gray-500">PLU {item.plu}</span>
                    )}
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    £{item.base_price?.toFixed(2) || item.price?.toFixed(2) || '0.00'}
                  </span>
                </div>
                {item.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                )}
                
                {/* Custom Fields Display */}
                {item.custom_fields && Object.keys(item.custom_fields).length > 0 && (
                  <div className="mb-3 space-y-1">
                    {Object.entries(item.custom_fields).map(([key, value]) => {
                      const fieldDef = customFieldDefinitions.find(f => f.name === key);
                      if (!fieldDef) return null;
                      return (
                        <div key={key} className="text-xs">
                          <span className="text-gray-500">{fieldDef.label}:</span>{' '}
                          <span className="text-gray-700">
                            {Array.isArray(value) ? value.join(', ') : String(value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center justify-between mt-4">
                  <span className={`px-2 py-1 rounded text-xs ${
                    item.available 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {item.available ? 'Available' : 'Unavailable'}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingItem(item);
                        setShowAddModal(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <MenuItemForm
          item={editingItem}
          customFieldDefinitions={customFieldDefinitions}
          categories={categories}
          onSave={handleSaveItem}
          onCancel={() => {
            setShowAddModal(false);
            setEditingItem(null);
          }}
        />
      )}
    </div>
  );
};

const MenuItemForm = ({ item, customFieldDefinitions, categories, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    description: item?.description || '',
    category: item?.category || '',
    base_price: item?.base_price || item?.price || 0,
    plu: item?.plu || '',
    image_url: item?.image_url || '',
    available: item?.available !== false,
    modifiers: item?.modifiers || [],
    allergens: item?.allergens || [],
    dietary_info: item?.dietary_info || [],
    custom_fields: item?.custom_fields || {}
  });

  const [newCategory, setNewCategory] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const renderCustomField = (fieldDef) => {
    const value = formData.custom_fields[fieldDef.name] || fieldDef.default_value || '';

    switch (fieldDef.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => setFormData({
              ...formData,
              custom_fields: { ...formData.custom_fields, [fieldDef.name]: e.target.value }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required={fieldDef.is_required}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            min={fieldDef.min_value}
            max={fieldDef.max_value}
            onChange={(e) => setFormData({
              ...formData,
              custom_fields: { ...formData.custom_fields, [fieldDef.name]: parseFloat(e.target.value) || 0 }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required={fieldDef.is_required}
          />
        );
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => setFormData({
              ...formData,
              custom_fields: { ...formData.custom_fields, [fieldDef.name]: e.target.value }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required={fieldDef.is_required}
          >
            <option value="">Select...</option>
            {fieldDef.options?.map((opt, idx) => (
              <option key={idx} value={typeof opt === 'string' ? opt : opt.value}>
                {typeof opt === 'string' ? opt : opt.label}
              </option>
            ))}
          </select>
        );
      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {fieldDef.options?.map((opt, idx) => {
              const optValue = typeof opt === 'string' ? opt : opt.value;
              const optLabel = typeof opt === 'string' ? opt : opt.label;
              return (
                <label key={idx} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={(e) => {
                      const newValues = e.target.checked
                        ? [...selectedValues, optValue]
                        : selectedValues.filter(v => v !== optValue);
                      setFormData({
                        ...formData,
                        custom_fields: { ...formData.custom_fields, [fieldDef.name]: newValues }
                      });
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
        );
      case 'boolean':
        return (
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => setFormData({
                ...formData,
                custom_fields: { ...formData.custom_fields, [fieldDef.name]: e.target.checked }
              })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span>{fieldDef.label}</span>
          </label>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">{item ? 'Edit Menu Item' : 'Add Menu Item'}</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
              <input aria-label="Name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea aria-label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
              <div className="flex space-x-2">
                <select aria-label="Category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newCategory.trim()) {
                        setFormData({ ...formData, category: newCategory.trim() });
                        setNewCategory('');
                      }
                    }
                  }}
                  placeholder="Or type new"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Base Price (£) *</label>
              <input aria-label="Base Price (£)"
                type="number"
                step="0.01"
                min="0"
                value={formData.base_price}
                onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PLU / item code</label>
              <input aria-label="PLU"
                type="text"
                maxLength={32}
                pattern="[A-Za-z0-9][A-Za-z0-9._\-]{0,31}"
                value={formData.plu}
                onChange={(e) => setFormData({ ...formData, plu: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. 1042"
              />
              <p className="text-xs text-gray-500 mt-1">Optional. Unique in your organisation; matched to POS item codes.</p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
              <input aria-label="Image URL"
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.available}
                  onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Available</span>
              </label>
            </div>
          </div>

          {/* Custom Fields */}
          {customFieldDefinitions.length > 0 && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-lg font-semibold mb-4">Custom Fields</h3>
              <div className="space-y-4">
                {customFieldDefinitions.map((fieldDef) => (
                  <div key={fieldDef.id || fieldDef.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {fieldDef.label}
                      {fieldDef.is_required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderCustomField(fieldDef)}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{item ? 'Update' : 'Create'} Item</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MenuItemManagement;
